import { useEffect, useMemo, useState, useRef } from "react";
import { Check, ChevronDown } from "lucide-react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import TableConfigForm from "./TableConfigForm";
import { MAIN_URL } from "../../../Configurations/Urls";
import { LOCATIONS_DATAGRID_KEY } from "../Location/locationListConstants";
import { DEPARTMENTS_DATAGRID_KEY } from "../Department/departmentListConstants";
import { DESIGNATIONS_DATAGRID_KEY } from "../Designations/designationListConstants";
import { WORK_SHIFTS_DATAGRID_KEY } from "../../Time & Attendence/WorkShifts/workShiftListConstants";
import { EMPLOYEES_DATAGRID_KEY } from "../../EmployeeManagement/Employees/employeeListConstants";
import { EMPLOYEE_EXITS_DATAGRID_KEY } from "../../EmployeeManagement/EmployeeExits/employeeExitListConstants";
import { ORGANIZATION_ENTITIES_DATAGRID_KEY } from "./datagridConfigUtils";

/** Organization Setup + Employee Management + EMS list grids. */
function buildTables(orgId) {
  return [
    {
      id: "organization-entities",
      tablename: "Organization Entities",
      tableheading: "organization-entities-list",
      apiFieldsConfig: `${MAIN_URL}/api/organizations/${orgId}/entity?mode=1`,
      apiPreviewConfig: `${MAIN_URL}/api/organizations/${orgId}/entity?mode=1`,
      datagridKeyProp: `${ORGANIZATION_ENTITIES_DATAGRID_KEY}_${orgId}`,
    },
    {
      id: "locations",
      tablename: "Locations",
      tableheading: "locations",
      apiFieldsConfig: `${MAIN_URL}/api/organizations/${orgId}/location?mode=1`,
      apiPreviewConfig: `${MAIN_URL}/api/organizations/${orgId}/location?mode=1`,
      datagridKeyProp: `${LOCATIONS_DATAGRID_KEY}_${orgId}`,
    },
    {
      id: "departments",
      tablename: "Departments",
      tableheading: "Departments",
      apiFieldsConfig: `${MAIN_URL}/api/organizations/${orgId}/department?mode=1`,
      apiPreviewConfig: `${MAIN_URL}/api/organizations/${orgId}/department?mode=1`,
      datagridKeyProp: `${DEPARTMENTS_DATAGRID_KEY}_${orgId}`,
    },
    {
      id: "designation",
      tablename: "Designations",
      tableheading: "Designation",
      apiFieldsConfig: `${MAIN_URL}/api/organizations/${orgId}/designation?mode=1`,
      apiPreviewConfig: `${MAIN_URL}/api/organizations/${orgId}/designation?mode=1`,
      datagridKeyProp: `${DESIGNATIONS_DATAGRID_KEY}_${orgId}`,
    },
    {
      id: "work_shifts",
      tablename: "Workshifts",
      tableheading: "work_shifts",
      apiFieldsConfig: `${MAIN_URL}/api/organizations/${orgId}/workshift?mode=1`,
      apiPreviewConfig: `${MAIN_URL}/api/organizations/${orgId}/workshift?mode=1`,
      datagridKeyProp: `${WORK_SHIFTS_DATAGRID_KEY}_${orgId}`,
    },
    {
      id: "employees",
      tablename: "Employees",
      tableheading: "employee-list",
      apiFieldsConfig: `${MAIN_URL}/api/organizations/${orgId}/employee?mode=2&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/organizations/${orgId}/employee?mode=2&per_page=25`,
      datagridKeyProp: `${EMPLOYEES_DATAGRID_KEY}_${orgId}`,
    },
    {
      id: "employee_exit",
      tablename: "Employee Exits",
      tableheading: "employee_exit",
      apiFieldsConfig: `${MAIN_URL}/api/organizations/${orgId}/employee-exit?mode=1`,
      apiPreviewConfig: `${MAIN_URL}/api/organizations/${orgId}/employee-exit?mode=1`,
      datagridKeyProp: `${EMPLOYEE_EXITS_DATAGRID_KEY}_${orgId}`,
    },
    {
      id: "ems-training-program-categories",
      tablename: "Training Program Categories",
      tableheading: "ems_training_program_categories",
      apiFieldsConfig: `${MAIN_URL}/api/ems/training-programs-categories?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/training-programs-categories?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_training_program_categories_grid_${orgId}`,
    },
    {
      id: "ems-training-programs",
      tablename: "Training Programs",
      tableheading: "ems_training_programs",
      apiFieldsConfig: `${MAIN_URL}/api/ems/training-programs?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/training-programs?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_training_programs_grid_${orgId}`,
    },
    {
      id: "ems-training-program-modules",
      tablename: "Training Program Modules",
      tableheading: "ems_training_program_modules",
      apiFieldsConfig: `${MAIN_URL}/api/ems/training-program-modules?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/training-program-modules?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_training_program_modules_grid_${orgId}`,
    },
    {
      id: "ems-module-questions",
      tablename: "Module Questions",
      tableheading: "ems_module_questions",
      apiFieldsConfig: `${MAIN_URL}/api/ems/module-questions?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/module-questions?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_module_questions_grid_${orgId}`,
    },
    {
      id: "ems-training-spaces",
      tablename: "Training Spaces",
      tableheading: "ems_training_spaces",
      apiFieldsConfig: `${MAIN_URL}/api/ems/training-spaces?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/training-spaces?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_training_spaces_grid_${orgId}`,
    },
    {
      id: "ems-students",
      tablename: "Students",
      tableheading: "ems_students",
      apiFieldsConfig: `${MAIN_URL}/api/ems/students?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/students?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_students_grid_${orgId}`,
    },
    {
      id: "ems-student-fees",
      tablename: "Fee Receipts",
      tableheading: "ems_student_fees",
      apiFieldsConfig: `${MAIN_URL}/api/ems/students-fees?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/students-fees?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_student_fees_grid_${orgId}`,
    },
    {
      id: "ems-leads",
      tablename: "Leads",
      tableheading: "ems_leads",
      apiFieldsConfig: `${MAIN_URL}/api/ems/leads?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/leads?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_leads_grid_${orgId}`,
    },
    {
      id: "ems-lead-stages",
      tablename: "Lead Stages",
      tableheading: "ems_lead_stages",
      apiFieldsConfig: `${MAIN_URL}/api/ems/lead-stages?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/lead-stages?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_lead_stages_grid_${orgId}`,
    },
    {
      id: "ems-lead-sources",
      tablename: "Lead Sources",
      tableheading: "ems_lead_sources",
      apiFieldsConfig: `${MAIN_URL}/api/ems/lead-sources?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/lead-sources?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_lead_sources_grid_${orgId}`,
    },
    {
      id: "ems-lead-activities",
      tablename: "Lead Activities",
      tableheading: "ems_lead_activities",
      apiFieldsConfig: `${MAIN_URL}/api/ems/lead-activities?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/lead-activities?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_lead_activities_grid_${orgId}`,
    },
    {
      id: "ems-lead-contact-timings",
      tablename: "Lead Contact Timing",
      tableheading: "ems_lead_contact_timings",
      apiFieldsConfig: `${MAIN_URL}/api/ems/lead-contact-timings?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/lead-contact-timings?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_lead_contact_timings_grid_${orgId}`,
    },
    {
      id: "ems-assessments",
      tablename: "Assesments",
      tableheading: "ems_assessments",
      apiFieldsConfig: `${MAIN_URL}/api/ems/assesments?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/assesments?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_assessments_grid_${orgId}`,
    },
    {
      id: "ems-assessment-results",
      tablename: "Assessment Results",
      tableheading: "ems_assessment_results",
      apiFieldsConfig: `${MAIN_URL}/api/ems/assesment-results?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/assesment-results?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_assessment_results_grid_${orgId}`,
    },
    {
      id: "ems-batches",
      tablename: "Batches",
      tableheading: "ems_batches",
      apiFieldsConfig: `${MAIN_URL}/api/ems/batches?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/batches?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_batches_grid_${orgId}`,
    },
    {
      id: "ems-batch-classes",
      tablename: "Batch Classes",
      tableheading: "ems_batch_classes",
      apiFieldsConfig: `${MAIN_URL}/api/ems/batch-classes?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/batch-classes?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_batch_classes_grid_${orgId}`,
    },
    {
      id: "ems-batch-class-records",
      tablename: "Class Records",
      tableheading: "ems_batch_class_records",
      apiFieldsConfig: `${MAIN_URL}/api/ems/class-records?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/class-records?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_batch_class_records_grid_${orgId}`,
    },
    {
      id: "ems-batch-students",
      tablename: "Batch Students",
      tableheading: "ems_batch_students",
      apiFieldsConfig: `${MAIN_URL}/api/ems/batch-students?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/batch-students?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_batch_students_grid_${orgId}`,
    },
    {
      id: "ems-recruitment-agencies",
      tablename: "Recruitment Agencies",
      tableheading: "ems_recruitment_agencies",
      apiFieldsConfig: `${MAIN_URL}/api/ems/recruitment-agencies?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/recruitment-agencies?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_recruitment_agencies_grid_${orgId}`,
    },
    {
      id: "ems-companies",
      tablename: "Companies",
      tableheading: "ems_companies",
      apiFieldsConfig: `${MAIN_URL}/api/ems/companies?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/companies?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_companies_grid_${orgId}`,
    },
    {
      id: "ems-admissions",
      tablename: "Admissions",
      tableheading: "ems_admissions",
      apiFieldsConfig: `${MAIN_URL}/api/ems/admissions?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/admissions?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_admissions_grid_${orgId}`,
    },
    {
      id: "ems-fee-installments",
      tablename: "Fee Installments",
      tableheading: "ems_fee_installments",
      apiFieldsConfig: `${MAIN_URL}/api/ems/fee-installments?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/fee-installments?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_fee_installments_grid_${orgId}`,
    },
    {
      id: "ems-class-attendance",
      tablename: "Classes Attendance",
      tableheading: "ems_class_attendance",
      apiFieldsConfig: `${MAIN_URL}/api/ems/classes-attendance?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/classes-attendance?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_class_attendance_grid_${orgId}`,
    },
    {
      id: "ems-certificates",
      tablename: "Certificates",
      tableheading: "ems_certificates",
      apiFieldsConfig: `${MAIN_URL}/api/ems/certificates?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/certificates?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_certificates_grid_${orgId}`,
    },
    {
      id: "ems-placement-referrals",
      tablename: "Placement Referrals",
      tableheading: "ems_placement_referrals",
      apiFieldsConfig: `${MAIN_URL}/api/ems/placement-referrals?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/placement-referrals?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_placement_referrals_grid_${orgId}`,
    },
    {
      id: "ems-demo-sessions",
      tablename: "Demo Sessions",
      tableheading: "ems_demo_sessions",
      apiFieldsConfig: `${MAIN_URL}/api/ems/demo-sessions?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/demo-sessions?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_demo_sessions_grid_${orgId}`,
    },
    {
      id: "ems-demo-participants",
      tablename: "Demo Participants",
      tableheading: "ems_demo_participants",
      apiFieldsConfig: `${MAIN_URL}/api/ems/demo-participants?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/demo-participants?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_demo_participants_grid_${orgId}`,
    },
    {
      id: "ems-student-review-platforms",
      tablename: "Review Platforms",
      tableheading: "ems_student_review_platforms",
      apiFieldsConfig: `${MAIN_URL}/api/ems/student-review-platforms?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/student-review-platforms?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_student_review_platforms_grid_${orgId}`,
    },
    {
      id: "ems-student-reviews",
      tablename: "Student Reviews",
      tableheading: "ems_student_reviews",
      apiFieldsConfig: `${MAIN_URL}/api/ems/student-reviews?organization_id=${orgId}&mode=1&per_page=5`,
      apiPreviewConfig: `${MAIN_URL}/api/ems/student-reviews?organization_id=${orgId}&mode=1&per_page=25`,
      datagridKeyProp: `ems_student_reviews_grid_${orgId}`,
    },
  ];
}

export default function MultiTableConfig() {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const orgId = org?.organization_id;

  const tables = useMemo(() => {
    if (!orgId) return [];
    return [...buildTables(orgId)].sort((a, b) =>
      a.tablename.localeCompare(b.tablename)
    );
  }, [orgId]);

  const [activeTableId, setActiveTableId] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (tables.length === 0) return;
    const stillValid = tables.some((t) => t.id === activeTableId);
    if (!activeTableId || !stillValid) {
      setActiveTableId(tables[0].id);
    }
  }, [tables, activeTableId]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const onPointerDown = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [dropdownOpen]);

  const activeTable = useMemo(
    () => tables.find((t) => t.id === activeTableId),
    [tables, activeTableId]
  );

  const handleSelectTable = (tableId) => {
    setActiveTableId(tableId);
    setDropdownOpen(false);
  };

  return (
    <div className="ems-dashboard ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Organization Datagrid Configuration</h1>
          <div className="ems-page-sub">
            <span>Organization Setup</span>
            {org?.organization_name ? (
              <>
                <span className="ems-dot" />
                <span>{org.organization_name}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="ems-section-divider" />

      <p className="ems-pv-intro">
        <b>Choose a list</b> — set default visible, filterable, sortable, and
        pinned columns for the organization.
      </p>

      <div className="ems-card ems-dg-select-card">
        <label className="ems-aa-label" id="ems-dg-table-label">
          Select DataGrid
        </label>
        <div className="ems-dg-dropdown" ref={dropdownRef}>
          <button
            type="button"
            className={`ems-fm-input ems-dg-dropdown-trigger${
              dropdownOpen ? " is-open" : ""
            }`}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            aria-labelledby="ems-dg-table-label"
            onClick={() => setDropdownOpen((open) => !open)}
          >
            <span className="ems-dg-dropdown-value">
              {activeTable?.tablename || "Select a datagrid"}
            </span>
            <ChevronDown size={16} strokeWidth={2} />
          </button>

          {dropdownOpen ? (
            <div className="ems-dg-dropdown-menu" role="listbox">
              <div className="ems-dg-dropdown-list">
                {tables.map((table) => {
                  const selected = table.id === activeTableId;
                  return (
                    <button
                      key={table.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`ems-dg-dropdown-option${
                        selected ? " is-selected" : ""
                      }`}
                      onClick={() => handleSelectTable(table.id)}
                    >
                      <span>{table.tablename}</span>
                      {selected ? <Check size={15} strokeWidth={2.5} /> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {activeTable ? (
        <TableConfigForm
          key={activeTable.datagridKeyProp}
          tablename={activeTable.tablename}
          tableheading={activeTable.tableheading}
          apiFieldsConfig={activeTable.apiFieldsConfig}
          apiPreviewConfig={activeTable.apiPreviewConfig}
          datagridKeyProp={activeTable.datagridKeyProp}
        />
      ) : null}
    </div>
  );
}
